'use client'

import { useCallback, useEffect } from 'react'

import type { GridApi } from 'ag-grid-community'

import { useActionPanel } from './ActionPanelProvider'
import { getActionPanelWidth } from './getActionPanelWidth'

/** All of an AG Grid event this reads, so callers need not build a whole one. */
type GridEventWithApi = { api: GridApi }

/**
 * Keeps the action column's grid state matching what the panel is showing: its
 * width in `wide` and `narrow`, and its visibility in `minimized`, where the
 * column is hidden rather than narrowed.
 *
 * Both go through the grid API rather than the column def: changing defs makes
 * AG Grid reset every column's state (see `useColumnPinning`), and an
 * `"api"`-sourced change is one `useAgGridMemory` never persists — so no
 * version bump is needed for either.
 *
 * An API-set value does not survive a change to `columnDefs`, though: AG Grid
 * rebuilds the columns and reapplies each def's own width and `hide` — putting
 * the width back to its static 260 — and does it *silently*, with the event
 * suppressed, so there is nothing on the column to observe. Restored user state
 * survives that rebuild; a value set over the top of it does not. Hence
 * re-asserting on `displayedColumnsChanged`, which does fire. "Reset Columns"
 * and "Autosize All Columns" land here for the same reason.
 *
 * `useLayoutColumnDefs` reads feature flags, so any flag that settles after
 * mount rebuilds the defs.
 *
 * Forcing the column visible is safe because nothing else may hide it: it is
 * `lockVisible` and `suppressMovable`, so there is no drag-to-hide and no
 * chooser to fight. `minimized` is the only thing that hides it, and only the
 * API can.
 */
export function useActionColumnState({
  colId,
  gridApi,
}: {
  colId: string
  gridApi: GridApi | null
}) {
  const { actionCount, variant } = useActionPanel()
  const isMinimized = variant === 'minimized'
  const width = getActionPanelWidth({ actionCount, variant })

  /** Wire to `onDisplayedColumnsChanged`, and to `onGridReady` after memory. */
  const handleGridEvent = useCallback(
    ({ api }: GridEventWithApi) => {
      const column = api.getColumn(colId)
      if (!column) return

      // Doubles as the loop guard, like the width's below: `setColumnsVisible`
      // fires `displayedColumnsChanged`, which comes straight back here.
      if (column.isVisible() === isMinimized) {
        api.setColumnsVisible([colId], !isMinimized)
      }

      // A hidden column is not sized, it is absent.
      if (width === null) return

      // Doubles as the loop guard: only ever re-asserts a genuine mismatch, so
      // a grid that already looks right is never touched.
      if (column.getActualWidth() === width) return

      api.applyColumnState({ state: [{ colId, width }] })
    },
    [colId, isMinimized, width]
  )

  useEffect(() => {
    if (!gridApi) return
    handleGridEvent({ api: gridApi })
  }, [handleGridEvent, gridApi])

  return { handleGridEvent }
}
